// IndexedDB local pra persistir histórico, posição salva, favoritos.
// O local-db é a FONTE DA VERDADE local (offline-first). Quando há conta Swipe
// logada, os writes também enfileiram no SYNC (camada ./sync) pra "continuar de
// onde parou" cross-device — best-effort, sem bloquear o write local.
//
// Isolamento por provider (CRÍTICO B2B): todas as keys são prefixadas com
// providerScope (`p:<code>` ou `d:<listName>`), e índices `by-providerScope`
// permitem listar só dados do provider atual. Sem isso, 2 providers com
// mesmo itemId (vem do Xtream, pode coincidir) cruzariam dados.
//
// Schema v3 (2026-05-01): adiciona providerScope + índices. Migration
// descarta dados v2 (não existe prod ainda — só beta interno).

import { generateUuidV4 } from '@app-iptv/device'
import type { DBSchema, IDBPDatabase } from 'idb'

import {
  fallbackClearChannelViewsAll,
  fallbackClearChannelViewsProviderPrefix,
  fallbackClearChannelViewsScope,
  fallbackReadChannelView,
  fallbackReadChannelViews,
  fallbackRemoveChannelView,
  fallbackUpsertChannelView,
} from './channel-views-fallback'
import {
  applyFavoritesOverlay,
  fallbackClearFavoritesAll,
  fallbackClearFavoritesWhere,
  fallbackFavoriteVerdict,
  fallbackForgetFavorite,
  fallbackReadFavoriteEntries,
  fallbackTombstoneFavorite,
  fallbackTombstoneFavoritesWhere,
  fallbackUpsertFavorite,
} from './favorites-fallback'
import { deleteDB, openDB } from './idb-native'
import { clearAllUnavailableVerdicts } from './lib/continue-unavailable-store'
import { flattenEpisodes, resolveContinuePoint, type EpisodeLike, type SeasonsMap } from './lib/next-episode'
import { isPersistableProgress } from './lib/playback-progress'
import { currentScope, scopedKey } from './scope'
import {
  enqueueDelete,
  enqueueProfileDelete,
  enqueueProfileUpsert,
  enqueueWrite,
} from './sync'
import { captureWrite, type WriteCapture } from './sync/capture'
import type { SyncKind } from './sync/types'
import {
  fallbackClearAll,
  fallbackClearProviderPrefix,
  fallbackClearScope,
  fallbackReadAllPositions,
  fallbackReadPosition,
  fallbackReadPositions,
  fallbackRemovePosition,
  fallbackRemovePositions,
  fallbackUpsertPosition,
} from './watch-progress-fallback'

export type ContentType = 'channel' | 'vod' | 'series-episode'

export interface PlaybackPosition {
  id: string // scopedKey(`${type}:${itemId}` ou `${type}:${seriesId}:${episodeId}`)
  providerScope: string // `p:<code>` ou `d:<listName>` — pra índice + filtro
  type: ContentType
  itemId: string
  seriesId?: string // só pra episódios
  episodeId?: string // só pra episódios
  seasonNumber?: number
  episodeNumber?: number
  positionSec: number
  durationSec: number
  lastWatchedAt: number // unix sec
  finished?: boolean // >=90% — UI mostra ✓ "Assistido"
  // Snapshot pra renderizar sem precisar de outra request
  title: string
  poster: string | null
}

export interface Favorite {
  id: string // scopedKey(`${type}:${itemId}`)
  providerScope: string
  type: 'channel' | 'vod' | 'series'
  itemId: string
  addedAt: number
  title: string
  poster: string | null
  categoryId?: string
}

export interface ChannelView {
  // Key também scopada — mesmo channelId em providers diferentes não cruza
  id: string // scopedKey(groupKey ?? channelId)
  providerScope: string
  /** ID da fonte específica usada por último (source-level). Player retoma
   *  exatamente na fonte que o user estava — preserva contexto se ele
   *  tinha trocado de source antes de fechar. */
  channelId: string
  count: number
  lastWatchedAt: number
  title: string
  logo: string | null
  categoryId: string
  /** Chave canônica do grupo (`GroupedChannel.key`) — identifica o canal
   *  pelo NOME canônico, não pela fonte. Quando o mesmo canal tem 3 fontes,
   *  todas compartilham o mesmo groupKey → 1 entrada em "Recentes", não 3.
   *  Opcional pra retrocompatibilidade: ChannelView v1 sem groupKey é
   *  tratado como entrada source-level no `getRecentChannels` (migração
   *  leve em runtime — ver doc lá). Novos writes sempre populam. */
  groupKey?: string | undefined
  /** Versão do schema desse record. v1=sem groupKey, v2=com groupKey. Permite
   *  identificar records antigos sem migração massiva. Default 1 quando ausente. */
  schemaVersion?: number | undefined
}

/**
 * Perfil local (família). Vincula-se a uma sessão (account = providerCode + username).
 * Tipo `kids` esconde conteúdo adulto. `adult` mostra tudo.
 */
export interface Profile {
  id: string // uuid
  account: string // `${providerCode}:${username}` — isola perfis por conta
  name: string
  avatar: string // emoji ou cor inicial
  kind: 'adult' | 'kids'
  createdAt: number
  /** PIN opcional por perfil. Espelha o `hasPin` do servidor (fonte da verdade
   *  do PIN). NUNCA guardamos hash nem o PIN em claro no IndexedDB — só este
   *  boolean, pra decidir se a TV pede o PIN ao entrar no perfil. Indefinido em
   *  perfis criados localmente antes do 1º sync (= sem PIN até o pull confirmar). */
  hasPin?: boolean
}

/**
 * Snapshot do progresso GLOBAL de uma série — 1 entrada por série
 * (independente de quantos episódios). Usado pra:
 * - Mostrar "Assistido" no card SE allEpisodesWatched && totalEpisodesAtTime === current
 * - Mostrar "Novo Episódio" SE allEpisodesWatched && totalEpisodesAtTime < current
 *
 * Atualizado quando:
 * - User termina um episódio (>=90%) → `savePosition()` chama `recomputeSeriesProgress`
 * - User abre detalhes da série → `recomputeSeriesProgress` (auto-corrige se
 *   provider adicionou eps novos)
 * Nos dois casos, SÓ se a série tiver ao menos uma posição (AP-152): sem posição
 * nenhuma não há snapshot, e o que existir sem posição é ignorado pelo leitor.
 *
 * `totalEpisodesAtTime` é o snapshot de quantos eps a série tinha quando
 * o último ep foi assistido — usado pra detectar novidades.
 */
export interface SeriesProgress {
  id: string // scopedKey(`series-progress:${seriesId}`)
  providerScope: string
  seriesId: string
  /** Última `lastWatchedAt` (unix sec) entre todos episódios assistidos. */
  lastWatchedAt: number
  /** True se o último ep da série (S maior, E maior dentro da S) tem
   *  finished=true. Recalculado a cada save/abertura de detalhes. */
  allEpisodesWatched: boolean
  /** Quantos episódios a série tinha no momento do último cálculo.
   *  Card compara com `totalEpisodes` atual (do catálogo carregado)
   *  pra detectar "novo episódio disponível". */
  totalEpisodesAtTime: number
  /** Snapshot pra renderizar sem precisar de outra request. */
  title: string
  poster: string | null
  /** PRÓXIMO episódio a assistir — primeiro ep (em ordem S/E) SEM position
   *  finished. Usado por `getContinueWatching` pra ANCORAR a série no
   *  "continuar assistindo" quando o último ep salvo já está finished mas
   *  ainda há episódios à frente. `undefined` quando todos os eps disponíveis
   *  estão assistidos (série some do continuar). Campos opcionais: IDB é
   *  schemaless, records antigos sem estes campos são tratados como "sem
   *  próximo computado" até o próximo recompute (abertura de detalhes). */
  nextEpisodeId?: string
  nextSeason?: number
  nextEpisode?: number
}

/**
 * Item unificado de "Continuar assistindo". Saída de `getContinueWatching`,
 * consumida por Home/Movies/Series sem cada tela repetir a regra de filtro.
 *
 * Dois modos:
 * - **Em andamento** (`isNextEpisode === false`): filme ou episódio com
 *   `positionSec`/`durationSec` reais → card mostra a barra discreta de
 *   progresso (igual filme).
 * - **Próximo episódio** (`isNextEpisode === true`): série cujo ep mais recente
 *   já está finished, mas ainda há um próximo ep não-iniciado → a série
 *   continua aparecendo no rail (não some), ancorada nesse próximo ep. Card
 *   limpo, SEM barra (progresso 0%) e SEM overlay sobre o poster. `nextSeason`/
 *   `nextEpisode` ficam como metadata (não há badge sobre o cover).
 */
export interface ContinueWatchingItem {
  kind: 'vod' | 'series'
  /** Alvo de navegação: vodId (filme) ou seriesId (série). */
  itemId: string
  /** Só pra séries — id da série (igual ao itemId, explicitado pra clareza). */
  seriesId?: string
  /** Episódio a retomar/iniciar (em andamento OU o próximo não-assistido). */
  episodeId?: string
  title: string
  poster: string | null
  lastWatchedAt: number
  positionSec: number
  durationSec: number
  /** Quando `isNextEpisode`: temporada/episódio do PRÓXIMO ep (metadata; não há
   *  mais badge sobre o cover). */
  nextSeason?: number
  nextEpisode?: number
  /** true → série ancorada no PRÓXIMO ep (sem barra, progresso 0%); false →
   *  filme/ep em andamento com barra. */
  isNextEpisode: boolean
}

interface Schema extends DBSchema {
  positions: {
    key: string
    value: PlaybackPosition
    indexes: { 'by-lastWatchedAt': number; 'by-providerScope': string }
  }
  favorites: {
    key: string
    value: Favorite
    indexes: {
      'by-addedAt': number
      'by-type': string
      'by-providerScope': string
    }
  }
  channelViews: {
    key: string
    value: ChannelView
    indexes: {
      'by-lastWatchedAt': number
      'by-count': number
      'by-providerScope': string
    }
  }
  profiles: {
    key: string // profile.id (já incluem providerCode no `account`)
    value: Profile
    indexes: { 'by-account': string }
  }
  seriesProgress: {
    key: string
    value: SeriesProgress
    indexes: { 'by-providerScope': string; 'by-lastWatchedAt': number }
  }
  /** P1 #3 do review auto-switch (2026-05-11): persiste a fonte
   *  ESCOLHIDA MANUALMENTE pelo user no SourceSwitcher. Diferente do
   *  manual TTL em memória (30min) — aqui sobrevive reload da sessão.
   *  Política B (decisão Fernando): se a fonte manual ESGOTAR (todas as
   *  tentativas falharam), o registro é APAGADO + nova fonte que
   *  funcionou vira default. Auto-switches durante a sessão NÃO gravam. */
  manualSources: {
    key: string // `${providerScope}::${groupKey}`
    value: ManualSource
    indexes: { 'by-providerScope': string }
  }
}

export interface ManualSource {
  id: string // scopedKey(`manual-source:${groupKey}`)
  providerScope: string
  groupKey: string
  channelId: string
  /** Quando user fez a escolha. Pode usar pra TTL futuro se quiser. */
  chosenAt: number
}

const DB_NAME = 'iptv-local'
// v6: scope agora inclui profileId. Stores antigas com keys
// `p:CODE::TYPE:ID` (sem profile) ficam órfãs — não dá pra atribuir a
// um perfil retroativamente sem ambiguidade. Apaga e recria limpa.
// Sem prod = sem migration. Schema atual: `p:CODE::pf:ID::TYPE:RAW`.
//
// v7 (2026-05-11): adiciona store `manualSources` (P1 #3 review
// auto-switch). Stores existentes NÃO são tocadas — só ADD novo store.
const DB_VERSION = 7
let _dbPromise: Promise<IDBPDatabase<Schema>> | null = null

/**
 * Ultima falha de abertura: instante (ms) + o erro, pra reservir durante a
 * janela de espera sem tocar o disco de novo. `null` = nao ha falha pendente.
 */
let _openFailure: { at: number; error: unknown } | null = null

/**
 * Janela de espera antes de TENTAR REABRIR o IDB depois de uma falha.
 *
 * -- O defeito que isto fecha --
 * Antes, `getDb()` guardava a promise da abertura em `_dbPromise` e nunca a
 * soltava. Uma unica rejeicao (quota estourada, storage evictado pelo webOS)
 * ficava CACHEADA: toda chamada seguinte recebia a MESMA rejeicao e o
 * IndexedDB inteiro morria em silencio pelo resto da sessao. O cabecalho do
 * `idb-native.ts` ja descrevia esse mecanismo -- a reescrita de la removeu a
 * CAUSA de entao (`new Proxy` da lib `idb`, Chr49+) e deixou de pe a ARMADILHA
 * que a transformava em permanente.
 *
 * -- Por que uma janela, e nao reabrir a cada chamada --
 * Soltar `_dbPromise` sem janela troca um defeito silencioso por um CARO: sao
 * 35 call-sites de `getDb()` neste arquivo (contados por `await getDb()`, sem a
 * linha da definicao; o escopo e ESTE arquivo — no repo inteiro os outros hits
 * sao os `getDb` privados dos modulos irmaos, que sao cinco funcoes distintas e
 * nao se somam), e uma unica entrada de tela dispara
 * uma rajada (favoritos + continuar assistindo + progresso de serie + recentes).
 * Num device com IDB permanentemente indisponivel, cada navegacao viraria ~10
 * aberturas falhas -- em cima de um orcamento de transicao de 200-300ms, nunca
 * >400 (docs/specs/ux-tv.md).
 *
 * -- O numero, e por que 30s --
 * A janela precisa ser MAIOR que a rajada pra colapsa-la em UMA tentativa: a
 * rajada cabe no orcamento de transicao (<=400ms), entao qualquer valor acima
 * disso ja resolve o custo. E precisa ser PEQUENA o bastante pra recuperacao
 * ser perceptivel DENTRO da sessao: quem usa "Limpar dados" (ou cujo SO liberou
 * quota) tem que voltar a favoritar sem reiniciar o app. 30s da 75x de folga
 * sobre a rajada e cabe numa visita de tela. Teto de tentativas numa sessao
 * longa de 2h: 2*3600/30 = 240, contra o ilimitado de hoje.
 *
 * -- Alternativas descartadas (registradas de proposito) --
 * - Reusar `MIN_AGGRESSIVE_INTERVAL_MS` (3h) do `idb-quota-manager`: aquele
 *   numero guarda uma operacao DESTRUTIVA e cara (eviction agressiva de LRU),
 *   onde repetir machuca. Reabrir um DB e barato e idempotente. Com 3h, quem
 *   liberou espaco continuaria quebrado pelo resto da sessao -- e o mesmo
 *   defeito "morto em silencio" que estamos removendo, so que com pavio longo.
 * - Backoff exponencial: exige estado por tentativa e uma politica de reset, e
 *   o teto que ele compraria nao e necessario (240 tentativas/2h ja e baixo).
 *   O que de fato custa e a RAJADA, e qualquer janela >400ms a colapsa por
 *   inteiro. Mais estado no piso Chr38 e mais superficie de bug sem ganho medido.
 */
export const DB_REOPEN_COOLDOWN_MS = 30_000

/** Rejeicao ja "tratada" (catch no-op), pra reservir a falha em janela sem
 *  gerar unhandled rejection num motor antigo caso algum caller esqueca. */
function rejectedWith(error: unknown): Promise<IDBPDatabase<Schema>> {
  const p = Promise.reject(error) as Promise<IDBPDatabase<Schema>>
  p.catch(() => {})
  return p
}

/** Zera o estado de falha -- a proxima chamada tenta abrir NA HORA. Usado por
 *  quem acabou de agir sobre a causa (hard reset / limpeza de dados). */
function clearOpenFailure(): void {
  _openFailure = null
}

/**
 * Apaga o DB local inteiro (positions/favorites/channelViews/profiles).
 * Usado pelo "Limpar dados" das Settings — quando algo dá ruim com sessão/cache
 * e o usuário precisa começar do zero. Não toca em token/zustand (o caller
 * cuida disso). */
export async function hardResetLocalDb(): Promise<void> {
  fallbackClearAll()
  fallbackClearChannelViewsAll()
  // O overlay de FAVORITOS NÃO sai aqui: ele guarda LÁPIDES, e elas escondem
  // registros que ainda estão no disco. Esta função só o tira depois do
  // `deleteDB` (ver o fim dela, e o limite disso no sistema, e o cabeçalho de
  // favorites-fallback.ts, "LIMPEZA EM MASSA"). Progresso e canais recentes
  // não têm lápide: limpá-los antes não desfaz nenhuma remoção anterior.
  clearAllUnavailableVerdicts()
  // Fecha conexão aberta antes de deletar (idb exige).
  // ⚠️ `_dbPromise` pode estar REJEITADA (foi justamente o IDB quebrado que
  // trouxe o usuário ao "Limpar dados"). Sem o try/catch, o await abaixo
  // lançaria e o hard reset — a única saída manual do estado ruim — falharia
  // exatamente quando é mais necessário.
  if (_dbPromise) {
    try {
      const db = await _dbPromise
      db.close()
    } catch {
      // Abertura nunca completou: não há conexão pra fechar. Segue pro delete.
    }
    _dbPromise = null
  }
  clearOpenFailure()
  await deleteDB(DB_NAME)
  // O banco saiu: nenhum registro sobrou pra lápide esconder. Se o `deleteDB`
  // rejeitar, a linha abaixo não roda e o overlay fica como estava AO FIM
  // DESTA FUNÇÃO.
  // ⚠️ Isso é o contrato da função, não do sistema. O único chamador hoje, o
  // "Limpar dados" do SettingsScreen (`doHardReset`), segue adiante mesmo com
  // a rejeição e faz `localStorage.clear()` logo depois: o overlay, lápides
  // incluídas, sai de qualquer jeito. Nesse caminho, o que impede o registro
  // que ficou no disco de voltar a aparecer não é a lápide, é o banco sair de
  // fato (a nova tentativa no boot seguinte, onde ela existir).
  fallbackClearFavoritesAll()
}

function getDb(): Promise<IDBPDatabase<Schema>> {
  if (_dbPromise) return _dbPromise
  // Falhou há pouco: devolve a mesma rejeição sem tocar o disco.
  if (_openFailure && Date.now() - _openFailure.at < DB_REOPEN_COOLDOWN_MS) {
    return rejectedWith(_openFailure.error)
  }
  // SEM `try` em volta da chamada, de propósito. O `openDB` do idb-native
  // SEMPRE devolve promise: o `indexedDB.open()` roda dentro do executor do
  // `new Promise`, e um throw SÍNCRONO dele (SecurityError com storage
  // bloqueado, `indexedDB` ausente) vira REJEIÇÃO. O `.catch` abaixo cobre esse
  // caminho e arma a janela como qualquer outra falha.
  // Já houve um `try` aqui, com a justificativa de que esse throw "furava a
  // janela". Não furava: com o idb-native real ele era inalcançável (o mutante
  // que o removia sobrevivia), e só um mock que LANÇAVA síncrono (coisa que o
  // módulo real não faz) o exercitava. Quem garante é o idb-native; quem prova
  // é o caso D de favorites-fallback.commit-abort.test.ts, com o idb-native
  // REAL e o `indexedDB.open` lançando.
  const attempt = openDB<Schema>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      // v3: stores antigas não têm providerScope. Apaga e recria — mais
      // simples que migração in-place, e como não há prod ainda, sem dor.
      if (oldVersion < 3) {
        for (const name of ['positions', 'favorites', 'channelViews'] as const) {
          if (db.objectStoreNames.contains(name)) db.deleteObjectStore(name)
        }
      }
      // v6: scope passa a incluir profileId. Keys antigas (`p:CODE::TYPE:ID`)
      // não têm profile pra associar — apaga stores que dependem de scope.
      // `profiles` mantém (é o catálogo de perfis, não scopado).
      if (oldVersion < 6) {
        for (const name of [
          'positions',
          'favorites',
          'channelViews',
          'seriesProgress',
        ] as const) {
          if (db.objectStoreNames.contains(name)) db.deleteObjectStore(name)
        }
      }
      if (!db.objectStoreNames.contains('positions')) {
        const s = db.createObjectStore('positions', { keyPath: 'id' })
        s.createIndex('by-lastWatchedAt', 'lastWatchedAt')
        s.createIndex('by-providerScope', 'providerScope')
      }
      if (!db.objectStoreNames.contains('favorites')) {
        const s = db.createObjectStore('favorites', { keyPath: 'id' })
        s.createIndex('by-addedAt', 'addedAt')
        s.createIndex('by-type', 'type')
        s.createIndex('by-providerScope', 'providerScope')
      }
      if (!db.objectStoreNames.contains('profiles')) {
        const s = db.createObjectStore('profiles', { keyPath: 'id' })
        s.createIndex('by-account', 'account')
      }
      if (!db.objectStoreNames.contains('channelViews')) {
        const s = db.createObjectStore('channelViews', { keyPath: 'id' })
        s.createIndex('by-lastWatchedAt', 'lastWatchedAt')
        s.createIndex('by-count', 'count')
        s.createIndex('by-providerScope', 'providerScope')
      }
      // v4: store de progresso global por série (1 entrada por série)
      if (!db.objectStoreNames.contains('seriesProgress')) {
        const s = db.createObjectStore('seriesProgress', { keyPath: 'id' })
        s.createIndex('by-providerScope', 'providerScope')
        s.createIndex('by-lastWatchedAt', 'lastWatchedAt')
      }
      // v7: manual sources persistentes (P1 #3 review auto-switch)
      if (!db.objectStoreNames.contains('manualSources')) {
        const s = db.createObjectStore('manualSources', { keyPath: 'id' })
        s.createIndex('by-providerScope', 'providerScope')
      }
    },
  })
  _dbPromise = attempt
  // SOLTA a promise em caso de falha — é isto que impede a rejeição de virar
  // permanente. O `_dbPromise === attempt` evita que uma tentativa VELHA que
  // rejeita tarde derrube a conexão de uma tentativa NOVA já bem-sucedida.
  attempt.catch((error: unknown) => {
    _openFailure = { at: Date.now(), error }
    if (_dbPromise === attempt) _dbPromise = null
  })
  return attempt
}

// ===== Positions =====

export const FINISHED_PROGRESS_THRESHOLD = 0.9

// Caller passa sem providerScope — adicionamos dentro.
type PlaybackPositionInput = Omit<PlaybackPosition, 'providerScope'>

/**
 * Mescla positions do IDB com o FALLBACK (localStorage) por id. Núcleo PURO
 * (testável sem storage) do leitor de "Continuar assistindo" resiliente à quota
 * de IDB estourada — no device do dono os writes recentes vivem só no fallback.
 *
 * Regras:
 *  - vence o registro com maior `lastWatchedAt` (o progresso mais recente);
 *    empate → mantém o do IDB (fonte canônica) pra evitar flapping.
 *  - `finished` é MONOTÔNICO: se qualquer fonte marcou finished=true, o
 *    resultado é finished=true (reassistir parcial nunca "desmarca" o ✓).
 */
export function mergePositions(
  idb: ReadonlyArray<PlaybackPosition>,
  fallback: ReadonlyArray<PlaybackPosition>,
): PlaybackPosition[] {
  const byId = new Map<string, PlaybackPosition>()
  for (const p of idb) byId.set(p.id, p)
  for (const p of fallback) {
    const existing = byId.get(p.id)
    if (!existing) {
      byId.set(p.id, p)
      continue
    }
    const winner = p.lastWatchedAt > existing.lastWatchedAt ? p : existing
    const finished = existing.finished === true || p.finished === true
    byId.set(p.id, finished ? { ...winner, finished: true } : winner)
  }
  return [...byId.values()]
}

/** Lê TODAS as positions do scope de AMBAS as fontes (IDB + fallback),
 *  mescladas. Tolerante a IDB quebrado (quota/eviction): se o read do IDB
 *  falhar, cai só no fallback. */
async function readMergedPositions(scope: string): Promise<PlaybackPosition[]> {
  let idb: PlaybackPosition[] = []
  try {
    const db = await getDb()
    idb = await db.getAllFromIndex('positions', 'by-providerScope', IDBKeyRange.only(scope))
  } catch {
    idb = []
  }
  return mergePositions(idb, fallbackReadPositions(scope))
}

/** Lê seriesProgress do scope, tolerante a IDB quebrado (não há fallback pra
 *  este store; o "Continuar assistindo" cai só na fonte (a) = positions). */
async function readScopedSeriesProgress(scope: string): Promise<SeriesProgress[]> {
  try {
    const db = await getDb()
    return await db.getAllFromIndex('seriesProgress', 'by-providerScope', IDBKeyRange.only(scope))
  } catch {
    return []
  }
}

/** `finished` anterior consultando IDB E fallback (monotônico entre as duas
 *  fontes). Tolerante a IDB quebrado. `scopedId` = `scope::rawId`. */
async function readPreviousFinished(scopedId: string): Promise<boolean> {
  if (fallbackReadPosition(scopedId)?.finished === true) return true
  try {
    const db = await getDb()
    const previous = await db.get('positions', scopedId)
    return previous?.finished === true
  } catch {
    return false
  }
}

/** Monta o PlaybackPosition final (id scopado + finished monotônico). O
 *  providerScope/id são fonte-da-verdade do scope da sessão (ignora o que o
 *  caller passou). */
function buildPositionRecord(
  p: PlaybackPositionInput,
  scope: string,
  previousFinished: boolean,
): PlaybackPosition {
  const ratio = p.durationSec > 0 ? p.positionSec / p.durationSec : 0
  const reachedThreshold = ratio >= FINISHED_PROGRESS_THRESHOLD
  return {
    ...p,
    id: `${scope}::${p.id}`,
    providerScope: scope,
    finished: reachedThreshold || previousFinished,
  }
}

/**
 * Grava um PlaybackPosition já montado. IDB é a fonte CANÔNICA (sincroniza
 * cross-device via outbox). Se o `put` REJEITAR (quota de IDB estourada /
 * storage "temporary" evictado pelo webOS — a causa do bug na LG do dono), cai
 * no FALLBACK localStorage. NUNCA lança — o player não pode quebrar pelo save.
 */
async function putPosition(record: PlaybackPosition, capture: WriteCapture): Promise<void> {
  try {
    const db = await getDb()
    await db.put('positions', record)
    // O dono e o relógio são os da ENTRADA da escrita (sync/capture.ts), não os de agora.
    void enqueueWrite({ localId: record.id, kind: 'position', payload: record, capture })
    // Sucesso no IDB → descarta a cópia de fallback (device saiu da pressão de
    // quota, ou nunca esteve nela). Mantém o fallback enxuto.
    fallbackRemovePosition(record.id)
  } catch {
    // IDB rejeitou. O sync (outbox) também é IDB → não adianta enfileirar aqui;
    // quando o IDB voltar, um novo save canônico re-enfileira. O que importa é
    // NÃO perder o progresso: espelha no localStorage (síncrono, quota separada).
    fallbackUpsertPosition(record)
  }
}

export async function savePosition(p: PlaybackPositionInput): Promise<void> {
  // O dono se grava na captura, antes de qualquer `await` (sync/capture.ts).
  const capture = captureWrite()
  const scope = currentScope()
  if (!scope) return // sem provider ativo (logged out) — no-op
  // Sem duração finita não há razão posição/duração honesta: `Infinity` gravado
  // deixava o item "em andamento" pra sempre (ratio 0, nunca finished) e virava
  // `null` no fallback JSON. O guard vale nos TRÊS pontos que gravam posição: aqui
  // (todo player, inclusive os que ainda não existem), no `saveFinalPosition` e no
  // `applyRemoteSyncItem` (o que chega de outro aparelho). Ver lib/playback-progress.ts.
  if (!isPersistableProgress(p.positionSec, p.durationSec)) return
  // CRÍTICO: NÃO desmarcar finished=true via progresso parcial. Cenário: user
  // terminou o filme/ep (finished=true) e clica "Assistir novamente"; sem essa
  // guarda o save de 5s a 10% sobrescreveria finished=false → o item sairia de
  // "Já Assistidos" e voltaria pra Continue. Único caminho pra desmarcar é a
  // API explícita (markVodWatched/markEpisodeWatched, deletePosition). Lê o
  // anterior das DUAS fontes (o item pode ter virado finished só no fallback).
  const previousFinished = await readPreviousFinished(`${scope}::${p.id}`)
  await putPosition(buildPositionRecord(p, scope, previousFinished), capture)
}

/**
 * Salva a posição FINAL de forma resiliente ao teardown AGRESSIVO do player (o
 * `destroy()`/`suspend()` do zapping roda logo após o cleanup do efeito). Grava
 * SÍNCRONO no fallback (localStorage) ANTES de qualquer await — garante a
 * posição mesmo se o IDB estiver lento/quebrado e o microtask do `put` não
 * completar antes do teardown — e por cima dispara o save canônico no IDB.
 * Retorna o promise do save canônico pra encadear (ex.: recomputeSeriesProgress).
 */
export function saveFinalPosition(p: PlaybackPositionInput): Promise<void> {
  const scope = currentScope()
  if (!scope) return Promise.resolve()
  // Mesmo guard do savePosition — ANTES do upsert síncrono no fallback, que não
  // passa por ele.
  if (!isPersistableProgress(p.positionSec, p.durationSec)) return Promise.resolve()
  const previousFinished = fallbackReadPosition(`${scope}::${p.id}`)?.finished === true
  // 1. Garantia SÍNCRONA: fallback ANTES do teardown. (finished monotônico é
  //    reforçado no read-merge, então uma cópia parcial aqui é segura.)
  fallbackUpsertPosition(buildPositionRecord(p, scope, previousFinished))
  // 2. Canônico assíncrono: IDB + sync + limpeza do fallback se der certo.
  return savePosition(p)
}

export async function getPosition(id: string): Promise<PlaybackPosition | undefined> {
  const scoped = scopedKey(id)
  if (!scoped) return undefined
  let hit: PlaybackPosition | undefined
  try {
    const db = await getDb()
    hit = await db.get('positions', scoped)
  } catch {
    hit = undefined
  }
  const fb = fallbackReadPosition(scoped)
  // Merge por id (o retomar tem que enxergar o progresso que só existe no
  // fallback num device com quota de IDB estourada).
  if (hit && fb) return mergePositions([hit], [fb])[0]
  return hit ?? fb
}

export async function deletePosition(id: string): Promise<void> {
  const capture = captureWrite()
  const scoped = scopedKey(id)
  if (!scoped) return
  // Remove das DUAS fontes — senão a cópia de fallback ressuscita o item no
  // read-merge do "Continuar assistindo".
  fallbackRemovePosition(scoped)
  try {
    const db = await getDb()
    await db.delete('positions', scoped)
  } catch {
    // IDB quebrado: o fallback já foi limpo, que é o que importa aqui.
  }
  void enqueueDelete({ localId: scoped, kind: 'position', capture })
}

// ===== Primitivas da migração de limpeza dos carimbos (autoadvance-stamp-migration) =====
// Operam por id LOCAL COMPLETO (qualquer scope do device) e NÃO enfileiram sync: a
// migração manda os tombstones ela mesma, lendo o desfecho de cada um.

/**
 * Todas as posições do DEVICE, de todos os scopes (IDB + fallback, mescladas por id).
 * LANÇA se o IDB não puder ser lido: pra migração, "não consegui ler" não pode virar
 * "não há nada" — ela precisa saber que a varredura foi parcial.
 */
export async function readAllPositionsAllScopes(): Promise<PlaybackPosition[]> {
  const db = await getDb()
  const idb = await db.getAll('positions')
  return mergePositions(idb, fallbackReadAllPositions())
}

/** Só as posições do fallback (todos os scopes) — varredura parcial quando o IDB não lê. */
export function readFallbackPositionsAllScopes(): PlaybackPosition[] {
  return fallbackReadAllPositions()
}

/** Apaga posições (IDB + fallback) por id LOCAL completo, sem enfileirar sync.
 *  Devolve os ids que o IDB não conseguiu apagar. */
export async function deletePositionsLocalOnly(
  scopedIds: ReadonlyArray<string>,
): Promise<{ failed: string[] }> {
  if (scopedIds.length === 0) return { failed: [] }
  fallbackRemovePositions(scopedIds)
  const failed: string[] = []
  let db: IDBPDatabase<Schema>
  try {
    db = await getDb()
  } catch {
    return { failed: [...scopedIds] }
  }
  for (const id of scopedIds) {
    try {
      await db.delete('positions', id)
    } catch {
      failed.push(id)
    }
  }
  return { failed }
}

/** Lê snapshots de progresso de série por id LOCAL completo (qualquer scope). Id que
 *  não existe (ou IDB que falha) simplesmente não aparece no mapa. */
export async function readSeriesProgressLocal(
  scopedIds: ReadonlyArray<string>,
): Promise<Map<string, SeriesProgress>> {
  const out = new Map<string, SeriesProgress>()
  if (scopedIds.length === 0) return out
  try {
    const db = await getDb()
    for (const id of scopedIds) {
      const rec = await db.get('seriesProgress', id)
      if (rec) out.set(id, rec)
    }
  } catch {
    // Sem IDB: o recálculo segue sem o anterior (título/pôster do catálogo ou das posições).
  }
  return out
}

/** Todos os snapshots de progresso de série do aparelho (TODOS os scopes). LANÇA se o
 *  IDB não lê: quem chama distingue "não há" de "não deu pra ler". */
export async function readAllSeriesProgressAllScopes(): Promise<SeriesProgress[]> {
  const db = await getDb()
  return db.getAll('seriesProgress')
}

/** Grava um snapshot de progresso de série no IDB, sem enfileirar sync (quem chama
 *  cuida do sync). Devolve se gravou. */
export async function putSeriesProgressLocalOnly(record: SeriesProgress): Promise<boolean> {
  try {
    const db = await getDb()
    await db.put('seriesProgress', record)
    return true
  } catch {
    return false
  }
}

/** Apaga snapshots de progresso de série por id LOCAL completo, sem enfileirar sync. */
export async function deleteSeriesProgressLocalOnly(
  scopedIds: ReadonlyArray<string>,
): Promise<{ failed: string[] }> {
  if (scopedIds.length === 0) return { failed: [] }
  const failed: string[] = []
  let db: IDBPDatabase<Schema>
  try {
    db = await getDb()
  } catch {
    return { failed: [...scopedIds] }
  }
  for (const id of scopedIds) {
    try {
      await db.delete('seriesProgress', id)
    } catch {
      failed.push(id)
    }
  }
  return { failed }
}

/** Id LOCAL do snapshot de progresso de uma série num scope (mesmo formato do
 *  `recomputeSeriesProgress`). */
export function seriesProgressLocalId(scope: string, seriesId: string): string {
  return `${scope}::series-progress:${seriesId}`
}

/** Marca um episódio como "já assistido" SEM precisar tocar.
 *  Cria/sobrescreve PlaybackPosition com finished=true. Usado pelo botão
 *  "Já vi" no drawer de episódios. */
export async function markEpisodeWatched(args: {
  seriesId: string
  episodeId: string
  seasonNumber?: number
  episodeNumber?: number
  title: string
  poster: string | null
}): Promise<void> {
  const capture = captureWrite()
  const scope = currentScope()
  if (!scope) return
  const rawId = `series-episode:${args.seriesId}:${args.episodeId}`
  const id = `${scope}::${rawId}`
  const lastWatchedAt = Math.floor(Date.now() / 1000)
  // Duração placeholder: o real será sobrescrito quando/se tocar de verdade.
  // O que importa é finished=true pra UI mostrar ✓ e excluir do "continuar".
  const record: PlaybackPosition = {
    id,
    providerScope: scope,
    type: 'series-episode',
    itemId: args.episodeId,
    seriesId: args.seriesId,
    episodeId: args.episodeId,
    ...(args.seasonNumber !== undefined ? { seasonNumber: args.seasonNumber } : {}),
    ...(args.episodeNumber !== undefined ? { episodeNumber: args.episodeNumber } : {}),
    positionSec: 1,
    durationSec: 1,
    lastWatchedAt,
    finished: true,
    title: args.title,
    poster: args.poster,
  }
  await putPosition(record, capture)
}

/** Análogo ao markEpisodeWatched mas pra VOD/filme. */
export async function markVodWatched(args: {
  vodId: string
  title: string
  poster: string | null
}): Promise<void> {
  const capture = captureWrite()
  const scope = currentScope()
  if (!scope) return
  const rawId = `vod:${args.vodId}`
  const id = `${scope}::${rawId}`
  const lastWatchedAt = Math.floor(Date.now() / 1000)
  const record: PlaybackPosition = {
    id,
    providerScope: scope,
    type: 'vod',
    itemId: args.vodId,
    positionSec: 1,
    durationSec: 1,
    lastWatchedAt,
    finished: true,
    title: args.title,
    poster: args.poster,
  }
  await putPosition(record, capture)
}

/** Posições recentes em andamento (não inclui finished). Usado em "Continue assistindo".
 *  Dedupe de séries: mantém só o episódio MAIS RECENTE por seriesId — caso contrário
 *  a row vira poluída quando o usuário troca de episódio (ex: abriu errado, voltou
 *  e abriu o certo). VOD/canal não deduplica (cada item é único por natureza). */
export async function getRecentPositions(limit = 20): Promise<PlaybackPosition[]> {
  const scope = currentScope()
  if (!scope) return []
  const inScope = await readMergedPositions(scope)
  // CRÍTICO: ordena tudo (incluindo finished) ANTES de deduplicar por
  // série. Senão um ep antigo NÃO-finished sobrescreve o ep mais recente
  // (que está finished) — Continue mostraria o progresso do ep antigo
  // mesmo o user tendo avançado e finalizado um episódio depois.
  // Cenário real: assistiu metade do Ep1 → assistiu Ep8 inteiro → Continue
  // exibia barra de progresso do Ep1 ❌. Agora: pega Ep8 (mais recente),
  // descobre que é finished, descarta a série de Continue (entra em
  // "Já Assistidos" via getFinishedSeries).
  const sorted = inScope.sort((a, b) => b.lastWatchedAt - a.lastWatchedAt)

  // 1ª passada: pra séries, mantém SÓ o ep mais recente (a "ponta" do
  // progresso do user). Filmes/canais entram todos.
  const seenSeries = new Set<string>()
  const latestPerSeries: PlaybackPosition[] = []
  for (const p of sorted) {
    if (p.type === 'series-episode' && p.seriesId) {
      if (seenSeries.has(p.seriesId)) continue
      seenSeries.add(p.seriesId)
    }
    latestPerSeries.push(p)
  }

  // 2ª passada: filtra finished — séries cujo ep mais recente está
  // finished saem de Continue (vão pra "Já Assistidos"). Filmes finished
  // também ficam fora (mesma seção).
  const result: PlaybackPosition[] = []
  for (const p of latestPerSeries) {
    if (p.finished) continue
    result.push(p)
    if (result.length >= limit) break
  }
  return result
}

/**
 * NÚCLEO PURO de "Continuar assistindo" (testável sem IDB). Combina as duas
 * fontes de verdade do progresso e devolve a lista unificada, deduplicada por
 * série, ordenada por `lastWatchedAt` desc.
 *
 * Fontes:
 *  (a) `positions` PARCIAIS (não-finished): filme em andamento + episódio em
 *      andamento. Aparecem com barra de progresso.
 *  (b) `seriesProgress` cuja série ainda tem episódio À FRENTE
 *      (`allEpisodesWatched === false` E `nextEpisodeId` presente). Ancora no
 *      PRÓXIMO ep não-assistido (badge "Próximo: TxEy", sem barra). Só entra
 *      série com AO MENOS UMA posição em `positions` (AP-152): o snapshot sem
 *      posição é o fantasma que a ficha gravava ao abrir série nunca assistida.
 *
 * Dedupe por série (regra do bug): uma série pode existir nas DUAS fontes
 *  ao mesmo tempo (ex.: ep em andamento + snapshot com próximo). Precedência:
 *  o EPISÓDIO EM ANDAMENTO vence (mostra a barra de onde o user parou); só
 *  caímos no "próximo episódio" (b) quando NÃO há ep em andamento pra aquela
 *  série. Isso conserta o caso TWD: último ep salvo virou finished → não há
 *  ep parcial → a série entra via (b), ancorada no próximo ep.
 *
 * Série 100% assistida (`allEpisodesWatched === true` ou sem `nextEpisodeId`)
 *  NÃO entra — vai pra "Já assistidos" via getFinishedSeries.
 */
export function buildContinueWatching(
  positions: ReadonlyArray<PlaybackPosition>,
  seriesProgress: ReadonlyArray<SeriesProgress>,
  limit = 20,
): ContinueWatchingItem[] {
  // Ordena tudo por recência ANTES de deduplicar — garante que o ep mais
  // recente de uma série seja o representante (mesma lógica de getRecentPositions).
  const sorted = [...positions].sort((a, b) => b.lastWatchedAt - a.lastWatchedAt)

  // 1ª passada nas positions: pra séries, mantém só o ep MAIS RECENTE; filmes
  // entram todos. Guarda qual ep é o representante por série (pra decidir
  // depois se está em andamento ou finished).
  const seenSeries = new Set<string>()
  const latestPerSeries: PlaybackPosition[] = []
  for (const p of sorted) {
    if (p.type === 'series-episode' && p.seriesId) {
      if (seenSeries.has(p.seriesId)) continue
      seenSeries.add(p.seriesId)
    }
    latestPerSeries.push(p)
  }

  const items: ContinueWatchingItem[] = []
  // Séries que JÁ entraram via ep em andamento (pra não duplicar via snapshot).
  const seriesInProgress = new Set<string>()

  // Fonte (a): positions parciais (filme + ep em andamento, não-finished).
  for (const p of latestPerSeries) {
    if (p.finished) continue
    if (p.type === 'vod') {
      items.push({
        kind: 'vod',
        itemId: p.itemId,
        title: p.title,
        poster: p.poster,
        lastWatchedAt: p.lastWatchedAt,
        positionSec: p.positionSec,
        durationSec: p.durationSec,
        isNextEpisode: false,
      })
    } else if (p.type === 'series-episode' && p.seriesId) {
      seriesInProgress.add(p.seriesId)
      items.push({
        kind: 'series',
        itemId: p.seriesId,
        seriesId: p.seriesId,
        // exactOptionalPropertyTypes: não gravar episodeId:undefined explícito.
        ...(p.episodeId !== undefined ? { episodeId: p.episodeId } : {}),
        title: p.title,
        poster: p.poster,
        lastWatchedAt: p.lastWatchedAt,
        positionSec: p.positionSec,
        durationSec: p.durationSec,
        isNextEpisode: false,
      })
    }
    // type === 'channel' não entra em "continuar assistindo" (rail própria).
  }

  // Fonte (b): séries de seriesProgress com episódio à frente, EXCETO as que
  // já entraram via ep em andamento. Ancora no PRÓXIMO ep não-assistido.
  for (const sp of seriesProgress) {
    // INVARIANTE (AP-152): o snapshot é DERIVADO das posições; sem nenhuma posição
    // da série (em andamento ou terminada, no IDB ou no fallback), ele não
    // representa nada assistido e não entra. É isto que neutraliza o snapshot
    // fantasma que a ficha gravava ao ABRIR uma série nunca assistida (próximo
    // T1E1, lastWatchedAt 0), inclusive o que já subiu pro servidor e volta pelo
    // pull, e o que sobra depois do "Limpar histórico" (apaga as posições, deixa
    // os snapshots). Sem tombstone: um tombstone apagaria o registro legítimo de
    // outro aparelho quando a ficha abre antes do pull. `seenSeries` tem TODA
    // série com posição (a 1ª passada não filtra finished).
    if (!seenSeries.has(sp.seriesId)) continue
    if (sp.allEpisodesWatched) continue // série 100% → "Já assistidos"
    if (!sp.nextEpisodeId) continue // sem próximo computado → não ancora
    if (seriesInProgress.has(sp.seriesId)) continue // já listada com barra
    items.push({
      kind: 'series',
      itemId: sp.seriesId,
      seriesId: sp.seriesId,
      episodeId: sp.nextEpisodeId,
      title: sp.title,
      poster: sp.poster,
      lastWatchedAt: sp.lastWatchedAt,
      positionSec: 0,
      durationSec: 0,
      ...(sp.nextSeason !== undefined ? { nextSeason: sp.nextSeason } : {}),
      ...(sp.nextEpisode !== undefined ? { nextEpisode: sp.nextEpisode } : {}),
      isNextEpisode: true,
    })
  }

  // Ordena a lista final (mistura das 2 fontes) por recência e corta no limite.
  items.sort((a, b) => b.lastWatchedAt - a.lastWatchedAt)
  return items.slice(0, limit)
}

/**
 * LEITOR ÚNICO de "Continuar assistindo" (IDB). Lê positions + seriesProgress
 * do scope atual e delega a combinação pro núcleo puro `buildContinueWatching`.
 * Home/Movies/Series consomem isto em vez de repetir a regra de filtro
 * (`!finished`) em cada tela.
 */
export async function getContinueWatching(limit = 20): Promise<ContinueWatchingItem[]> {
  const scope = currentScope()
  if (!scope) return []
  const [positions, seriesProgress] = await Promise.all([
    readMergedPositions(scope),
    readScopedSeriesProgress(scope),
  ])
  return buildContinueWatching(positions, seriesProgress, limit)
}

/** Key estável de React/foco pra um ContinueWatchingItem (substitui o
 *  `position.id` que não existe mais no item unificado). Único por (kind,
 *  série/filme, episódio) — séries em "próximo ep" e "em andamento" geram
 *  keys distintas, e o dedupe por série já garante 1 item por série na lista. */
export function continueItemKey(item: ContinueWatchingItem): string {
  return `${item.kind}:${item.itemId}:${item.episodeId ?? ''}`
}

/**
 * Decide o selo "Próximo: TxEy" de um ContinueWatchingItem — a LIGAÇÃO entre
 * `buildContinueWatching` e o VodCard, isolada como função pura pra ser testável
 * sem renderizar a tela (o bug do #446 passou porque o cálculo do label vivia
 * inline no JSX e a ligação não tinha teste).
 *
 * Regra: SÓ o ep "próximo" (série cujo ep mais recente terminou e o seguinte
 * ainda está em 0% → `isNextEpisode === true`, com temporada/episódio
 * conhecidos) ganha o selo. Ep/filme em andamento (`isNextEpisode === false`)
 * usa a barra de progresso e retorna `undefined` aqui.
 *
 * `translate` é injetado (normalmente `t` do i18n) pra manter a função pura e
 * respeitar os 3 locales. Retorna `undefined` quando não há selo a mostrar.
 */
export function continueNextEpisodeLabel(
  item: Pick<ContinueWatchingItem, 'isNextEpisode' | 'nextSeason' | 'nextEpisode'>,
  translate: (key: string, vars: { season: number; episode: number }) => string,
): string | undefined {
  if (!item.isNextEpisode) return undefined
  if (item.nextSeason === undefined || item.nextEpisode === undefined) return undefined
  return translate('home.nextEpisodeBadge', {
    season: item.nextSeason,
    episode: item.nextEpisode,
  })
}

/** Todas as posições de uma série (assistidas + em andamento). */
export async function getPositionsForSeries(seriesId: string): Promise<PlaybackPosition[]> {
  const scope = currentScope()
  if (!scope) return []
  const inScope = await readMergedPositions(scope)
  return inScope.filter((p) => p.type === 'series-episode' && p.seriesId === seriesId)
}

/** Filmes terminados (>=90% assistido). Sort por lastWatchedAt desc.
 *  Usado na seção "Já Assistidos" do Minha Lista. */
export async function getFinishedVod(limit = 50): Promise<PlaybackPosition[]> {
  const scope = currentScope()
  if (!scope) return []
  const inScope = await readMergedPositions(scope)
  return inScope
    .filter((p) => p.type === 'vod' && p.finished)
    .sort((a, b) => b.lastWatchedAt - a.lastWatchedAt)
    .slice(0, limit)
}

/** `itemId` dos filmes terminados (PURO). */
function finishedVodIdsOf(positions: ReadonlyArray<PlaybackPosition>): Set<string> {
  return new Set(positions.filter((p) => p.type === 'vod' && p.finished).map((p) => p.itemId))
}

/** Lookup batch de "vod finished" — retorna Set<itemId> pra renderizar
 *  cards rapidamente sem N reads individuais. Usar via useWatchedStatus
 *  (cliente). */
export async function getFinishedVodIds(): Promise<Set<string>> {
  const scope = currentScope()
  if (!scope) return new Set()
  return finishedVodIdsOf(await readMergedPositions(scope))
}

// ===== Series Progress (global por série) =====

/**
 * AP-152, a INVARIANTE dos leitores do snapshot (PURO): as séries com AO MENOS UMA
 * posição, em andamento ou terminada. `positions` vem de `readMergedPositions`
 * (IDB + fallback de localStorage).
 *
 * O snapshot (`seriesProgress`) é DERIVADO das posições. Sem nenhuma, ele não
 * representa nada assistido: é o fantasma que a ficha gravava ao abrir uma série
 * nunca vista, ou o que sobra depois do "Limpar histórico" (apaga as posições e
 * deixa os snapshots). Todo leitor que decide o que MOSTRAR a partir do snapshot
 * ("Continuar assistindo", "Já assistidos", o selo "Assistido"/"Novo Episódio")
 * passa por aqui. O predicado é o mesmo do `seenSeries` de `buildContinueWatching`
 * e do `getPositionsForSeries` que o recompute usa.
 */
export function seriesIdsWithPosition(
  positions: ReadonlyArray<Pick<PlaybackPosition, 'type' | 'seriesId'>>,
): Set<string> {
  const out = new Set<string>()
  for (const p of positions) {
    if (p.type === 'series-episode' && p.seriesId) out.add(p.seriesId)
  }
  return out
}

/** Só os snapshots que a invariante acima deixa passar (PURO). */
export function snapshotsWithPosition(
  snapshots: ReadonlyArray<SeriesProgress>,
  positions: ReadonlyArray<Pick<PlaybackPosition, 'type' | 'seriesId'>>,
): SeriesProgress[] {
  const withPosition = seriesIdsWithPosition(positions)
  return snapshots.filter((sp) => withPosition.has(sp.seriesId))
}

/** Lookup do progresso global de uma série. `undefined` também quando a série
 *  não tem posição nenhuma (AP-152). */
export async function getSeriesProgress(seriesId: string): Promise<SeriesProgress | undefined> {
  const scope = currentScope()
  if (!scope) return undefined
  const db = await getDb()
  const [record, positions] = await Promise.all([
    db.get('seriesProgress', `${scope}::series-progress:${seriesId}`),
    readMergedPositions(scope),
  ])
  if (!record) return undefined
  return snapshotsWithPosition([record], positions)[0]
}

/**
 * O que o `useWatchedStatus` desenha nos cards: filmes terminados + snapshot por
 * série (selo "Assistido"/"Novo Episódio"). As posições são lidas UMA vez e servem
 * às duas metades: o filtro do AP-152 não custa uma leitura a mais por instância
 * do hook (há uma por fileira, e a `LazyCategoryRow` monta no scroll).
 * Série sem posição não entra no mapa (a invariante de `seriesIdsWithPosition`).
 * Rejeita se o IDB não abre, como antes (o hook fica com os mapas vazios).
 */
export async function getWatchedStatus(): Promise<{
  finishedVod: Set<string>
  seriesMap: Map<string, SeriesProgress>
}> {
  const scope = currentScope()
  if (!scope) return { finishedVod: new Set(), seriesMap: new Map() }
  const db = await getDb()
  const [positions, snapshots] = await Promise.all([
    readMergedPositions(scope),
    db.getAllFromIndex('seriesProgress', 'by-providerScope', IDBKeyRange.only(scope)),
  ])
  return {
    finishedVod: finishedVodIdsOf(positions),
    seriesMap: new Map(snapshotsWithPosition(snapshots, positions).map((p) => [p.seriesId, p])),
  }
}

/** Lookup batch: Map<seriesId, SeriesProgress> pra renderizar grids
 *  sem N reads. É o `seriesMap` de `getWatchedStatus` (com a invariante). */
export async function getAllSeriesProgress(): Promise<Map<string, SeriesProgress>> {
  return (await getWatchedStatus()).seriesMap
}

/** Séries totalmente assistidas (último ep finished) — sort por
 *  lastWatchedAt desc. Pra seção "Já Assistidos" de séries. Série sem posição
 *  nenhuma não entra (AP-152): depois do "Limpar histórico" ela sai daqui. */
export async function getFinishedSeries(limit = 50): Promise<SeriesProgress[]> {
  const scope = currentScope()
  if (!scope) return []
  const db = await getDb()
  const [snapshots, positions] = await Promise.all([
    db.getAllFromIndex('seriesProgress', 'by-providerScope', IDBKeyRange.only(scope)),
    readMergedPositions(scope),
  ])
  return snapshotsWithPosition(snapshots, positions)
    .filter((p) => p.allEpisodesWatched)
    .sort((a, b) => b.lastWatchedAt - a.lastWatchedAt)
    .slice(0, limit)
}

/** Episódio mínimo pra ordenação S/E. */
type EpisodeRef = { episodeId: string; seasonNumber: number; episodeNumber: number }

/**
 * NÚCLEO PURO do ancoramento do "continuar assistindo" de séries (testável sem
 * IDB). Decide qual é o PRÓXIMO episódio que o card deve apontar, ANCORANDO no
 * ep MAIS RECENTE que o usuário tocou (maior `lastWatchedAt`), nunca varrendo
 * "o primeiro buraco do início". Restaura o comportamento original:
 *
 *  - Anchor EM ANDAMENTO (não finished) → próximo = ELE MESMO (card mostra onde
 *    o user parou, com barra). Ex.: user no ep16 parcial → card aponta o 16.
 *  - Anchor FINISHED → próximo = o SEGUINTE a ele na ordem S/E (primeiro à frente
 *    ainda não finished). Ex.: user concluiu o 16 (pulando anteriores) → 17,
 *    NUNCA S1E1.
 *  - Sem nenhuma position tocada (cold start) → primeiro ep ASC (S1E1). Só aqui
 *    "do início" é o correto, porque nada foi assistido ainda.
 *
 * Retorna `undefined` quando não há próximo (anchor é o último finished, ou tudo
 * à frente já está finished) → a série não ancora e sai do continuar.
 */
export function computeNextEpisode(
  episodes: ReadonlyArray<EpisodeRef>,
  positions: ReadonlyArray<Pick<PlaybackPosition, 'episodeId' | 'finished' | 'lastWatchedAt'>>,
): EpisodeRef | undefined {
  const ascending = [...episodes].sort((a, b) => {
    if (a.seasonNumber !== b.seasonNumber) return a.seasonNumber - b.seasonNumber
    return a.episodeNumber - b.episodeNumber
  })
  if (ascending.length === 0) return undefined

  // A DECISÃO mora em lib/next-episode (`resolveContinuePoint`) — a MESMA que a
  // ficha da série usa. Estas duas linhas são o conserto do "na série marca
  // certo, na home não": enquanto a regra estava escrita aqui também, ela
  // divergia da ficha ao pular episódios já finished, e quem re-assiste uma
  // série marcada como vista era teletransportado (a conta do dono guardava
  // T3:E7 no snapshot com ele na T2:E2). Nunca reescreva a regra aqui.
  const point = resolveContinuePoint(
    ascending.map((e) => e.episodeId),
    positions,
  )
  switch (point.kind) {
    // 'resume' = o ep em andamento (o card mostra a barra de onde parou);
    // 'next' = o seguinte na ordem.
    case 'resume':
    case 'next':
      return ascending[point.index]
    // Terminou o último do catálogo → não ancora, sai do "Continuar".
    case 'finished':
      return undefined
    // Nada tocado (ou nenhuma position casa com o catálogo atual, ex.: provider
    // rotacionou os ids): começa do primeiro. Só aqui "do início" é correto.
    case 'untouched':
      return ascending[0]
  }
}

/**
 * Os episódios do catálogo como o registro de progresso da série os lê (PURO). UMA regra
 * pra ficha, player e migração: antes eram três (a ficha mapeava temporada não numérica
 * pra 0 e a contava, a migração a DESCARTAVA, o player relia a chave e o `episode_num`
 * crus), e a série com temporada "Especiais" ganhava `totalEpisodesAtTime` diferente
 * conforme quem gravou, com badge "Novo Episódio" falso (sonda PAR do review, 29/09/2026).
 * Temporada/episódio não numéricos viram 0: entram na contagem e ficam fora da escolha do
 * último episódio (ver `buildSeriesProgress`).
 */
export function seriesProgressEpisodes<T extends EpisodeLike>(seasons: SeasonsMap<T>): EpisodeRef[] {
  return flattenEpisodes(seasons).map((e) => ({
    episodeId: e.id,
    seasonNumber: Number.isFinite(e.seasonNumber) ? e.seasonNumber : 0,
    episodeNumber: Number.isFinite(e.episodeNumber) ? e.episodeNumber : 0,
  }))
}

/**
 * NÚCLEO PURO do `SeriesProgress`: episódios do catálogo + posições da série → registro.
 * O único lugar da regra: o `recomputeSeriesProgress` (ficha e player) e o
 * `deriveSeriesProgress` (a migração dos carimbos) chamam este.
 *
 * - `lastWatchedAt` = o maior entre os `finished` (0 se nenhum).
 * - "Último" episódio = S maior, dentro dela E maior, entre os de temporada e episódio
 *   `> 0` (os 0, não numéricos ou especiais "0", só contam se não houver outro: sem
 *   isso virariam o "último"). `allEpisodesWatched` = ele está `finished`.
 * - Próximo = `computeNextEpisode` (ancora no mais recente tocado; nunca o 1º buraco).
 * - `totalEpisodesAtTime` = TODOS os episódios do catálogo (o card compara com o total
 *   atual pra detectar "novo episódio").
 * - `episodes` vazio = sem catálogo à mão: sem `next*`, `allEpisodesWatched: false`
 *   (não dá pra saber qual é o último) e o total que o chamador der.
 */
export function buildSeriesProgress(args: {
  id: string
  providerScope: string
  seriesId: string
  title: string
  poster: string | null
  episodes: ReadonlyArray<EpisodeRef>
  positions: ReadonlyArray<Pick<PlaybackPosition, 'episodeId' | 'finished' | 'lastWatchedAt'>>
  /** O `totalEpisodesAtTime` quando não há catálogo (`episodes` vazio). */
  totalWithoutCatalog: number
}): SeriesProgress {
  const lastWatchedAt = args.positions
    .filter((p) => p.finished)
    .reduce((max, p) => Math.max(max, p.lastWatchedAt), 0)
  let allEpisodesWatched = false
  let next: EpisodeRef | undefined
  if (args.episodes.length > 0) {
    const valid = args.episodes.filter((e) => e.seasonNumber > 0 && e.episodeNumber > 0)
    const pool = valid.length > 0 ? valid : args.episodes
    const last = [...pool].sort((a, b) =>
      a.seasonNumber !== b.seasonNumber ? b.seasonNumber - a.seasonNumber : b.episodeNumber - a.episodeNumber,
    )[0]
    allEpisodesWatched =
      last !== undefined && args.positions.some((p) => p.episodeId === last.episodeId && p.finished)
    // PRÓXIMO episódio a assistir: ANCORA no ep MAIS RECENTE tocado, não no "primeiro
    // buraco do início" (o #446 quebrou isso varrendo do começo, o que levava o user
    // que pula episódios de volta pro S1E1).
    next = computeNextEpisode(pool, args.positions)
  }
  return {
    id: args.id,
    providerScope: args.providerScope,
    seriesId: args.seriesId,
    lastWatchedAt,
    allEpisodesWatched,
    totalEpisodesAtTime: args.episodes.length > 0 ? args.episodes.length : args.totalWithoutCatalog,
    title: args.title,
    poster: args.poster,
    // Só popula `next*` quando há um próximo ep não-assistido E nem tudo está
    // assistido (espelha o que getContinueWatching exige pra exibir). Spread
    // condicional por causa do exactOptionalPropertyTypes (não gravar undefined).
    ...(next && !allEpisodesWatched
      ? { nextEpisodeId: next.episodeId, nextSeason: next.seasonNumber, nextEpisode: next.episodeNumber }
      : {}),
  }
}

/**
 * Recalcula e persiste o SeriesProgress de uma série (a regra: `buildSeriesProgress`).
 *
 * `seasons` = o catálogo CRU da série (`seasons` do series_info). Quem chama passa o
 * mapa, nunca uma lista já mapeada: a normalização é uma só (`seriesProgressEpisodes`).
 *
 * Chamar quando: (a) user termina um ep (savePosition com finished=true),
 * (b) user abre detalhes da série.
 *
 * Série sem NENHUMA posição (IDB + fallback): não grava nem enfileira (AP-152).
 */
export async function recomputeSeriesProgress<T extends EpisodeLike>(args: {
  seriesId: string
  title: string
  poster: string | null
  seasons: SeasonsMap<T>
}): Promise<void> {
  const capture = captureWrite()
  const scope = currentScope()
  if (!scope) return
  const episodes = seriesProgressEpisodes(args.seasons)
  if (episodes.length === 0) return // sem catálogo, no-op
  const db = await getDb()
  const positions = await getPositionsForSeries(args.seriesId)
  // AP-152: sem NENHUMA posição da série não há o que resumir. Antes, abrir a ficha
  // de uma série nunca assistida gravava (e enfileirava pro servidor) um snapshot
  // "próximo: T1E1, lastWatchedAt 0", e a série aparecia em "Continuar assistindo".
  // Não grava nem apaga: um tombstone aqui apagaria o registro legítimo de outro
  // aparelho se a ficha abrir antes do pull trazer as posições. O snapshot que já
  // existe sem posição fica neutro no leitor (`buildContinueWatching`). É a mesma
  // regra do `deriveSeriesProgress` da migração (nada sobrou → sem registro).
  // `getPositionsForSeries` já mescla IDB + fallback de localStorage.
  if (positions.length === 0) return
  const id = `${scope}::series-progress:${args.seriesId}`
  const record = buildSeriesProgress({
    id,
    providerScope: scope,
    seriesId: args.seriesId,
    title: args.title,
    poster: args.poster,
    episodes,
    positions,
    totalWithoutCatalog: 0,
  })
  try {
    await db.put('seriesProgress', record)
    // Relógio LWW = momento da escrita (ms, dentro do enqueueWrite). O lastWatchedAt
    // do conteúdo vai no payload. Recompute por abertura de detalhes (sem novo
    // assistido) também propaga o snapshot atualizado (totalEpisodesAtTime).
    void enqueueWrite({ localId: id, kind: 'series_progress', payload: record, capture })
  } catch {
    // IDB com quota estourada: o snapshot da série (fonte "próximo episódio")
    // não persiste, mas o "Continuar assistindo" segue funcionando pela fonte
    // (a) = positions, que tem fallback. Auto-corrige quando o IDB voltar
    // (recompute na próxima abertura de detalhes). Nunca quebra o player.
  }
}

// ===== Favorites =====

// Caller passa sem providerScope — adicionamos dentro a partir da sessão.
type FavoriteInput = Omit<Favorite, 'providerScope'>

/**
 * Todas as quatro operações de favorito são RESILIENTES ao IDB morto (quota
 * estourada / storage evictado pelo webOS), pelo mesmo contrato do progresso:
 * o IDB é canônico, e o `favorites-fallback` (localStorage) cobre a janela em
 * que ele recusa.
 *
 * ⚠️ As LEITURAS também. Uma leitura que rejeita quebra a tela do mesmo jeito
 * que uma escrita: era `isFavorite` rejeitando que deixava a estrela sem pintar
 * mesmo em item já favoritado.
 *
 * @returns `true` se persistiu em ALGUMA fonte (IDB ou fallback). `false` só
 * quando as duas recusaram — aí a estrela otimista tem que voltar.
 */
export async function addFavorite(f: FavoriteInput): Promise<boolean> {
  const capture = captureWrite()
  const scope = currentScope()
  if (!scope) return false
  // f.id já vem scopado (caller usa favoriteId() que scopa).
  const record = { ...f, providerScope: scope }
  let persisted: boolean
  try {
    const db = await getDb()
    await db.put('favorites', record)
    // Gravou no canônico → o overlay não tem mais o que dizer sobre este id.
    fallbackForgetFavorite(record.id)
    persisted = true
  } catch {
    persisted = fallbackUpsertFavorite(record)
  }
  void enqueueWrite({ localId: f.id, kind: 'favorite', payload: record, capture })
  return persisted
}

/** @returns `true` se a remoção persistiu em ALGUMA fonte. Ver `addFavorite`. */
export async function removeFavorite(id: string): Promise<boolean> {
  const capture = captureWrite()
  const scope = currentScope()
  let persisted: boolean
  try {
    const db = await getDb()
    // id já vem scopado (caller usa favoriteId)
    await db.delete('favorites', id)
    // Saiu do canônico → descarta qualquer add pendente no overlay, senão ele
    // ressuscitaria o item na próxima leitura.
    fallbackForgetFavorite(id)
    persisted = true
  } catch {
    // ⚠️ O delete NÃO chegou ao disco: o registro pode continuar no IDB. Sem a
    // LÁPIDE, o próximo `getFavorites()` o leria de volta e o favorito que o
    // usuário acabou de tirar RESSUSCITARIA.
    persisted = scope ? fallbackTombstoneFavorite(id, scope) : false
  }
  void enqueueDelete({ localId: id, kind: 'favorite', capture })
  return persisted
}

export async function isFavorite(id: string): Promise<boolean> {
  // O overlay é mais novo que o disco para os ids que ele conhece — e sabe
  // dizer "removido" (lápide), não só "existe".
  const overlay = fallbackFavoriteVerdict(id)
  if (overlay !== undefined) return overlay
  try {
    const db = await getDb()
    return (await db.get('favorites', id)) !== undefined
  } catch {
    return false
  }
}

async function readFavoritesInScope(scope: string): Promise<Favorite[]> {
  let fromIdb: Favorite[] = []
  try {
    const db = await getDb()
    fromIdb = await db.getAllFromIndex(
      'favorites',
      'by-providerScope',
      IDBKeyRange.only(scope),
    )
  } catch {
    // IDB morto: a lista vem só do overlay. Melhor uma lista parcial que uma
    // tela quebrada — e sem o overlay ela seria vazia de qualquer jeito.
    fromIdb = []
  }
  return applyFavoritesOverlay(fromIdb, fallbackReadFavoriteEntries(scope))
}

export async function getFavorites(): Promise<Favorite[]> {
  const scope = currentScope()
  if (!scope) return []
  return readFavoritesInScope(scope)
}

export async function getFavoritesByType(type: Favorite['type']): Promise<Favorite[]> {
  const scope = currentScope()
  if (!scope) return []
  // Filtra por scope no índice + filter por type em memória.
  // (alternativa seria índice composto, mas overhead não vale pra <100 itens)
  // O filtro por type vem DEPOIS da sobreposição: uma lápide precisa apagar o
  // item antes de qualquer recorte, senão ela some junto com o filtro.
  const all = await readFavoritesInScope(scope)
  return all.filter((f) => f.type === type)
}

/**
 * Gera id de favorito **scopado pelo provider atual**. Usado pelo caller pra
 * todas operações (add/remove/isFavorite). Retorna chave fictícia sem-scope
 * só pra evitar undefined em early returns — nunca casa no DB.
 */
export function favoriteId(type: Favorite['type'], itemId: string): string {
  const scope = currentScope() ?? '__noscope__'
  return `${scope}::${type}:${itemId}`
}

// ===== Channel views (pra "Canais recentes") =====

// Caller passa o que sabe — id/scope/count/timestamp/schemaVersion adicionados
// aqui dentro. `groupKey` é fortemente recomendado em writes novos: sem ele,
// o mesmo canal em fontes alternativas vira N entradas em "Recentes".
type ChannelViewInput = Pick<
  ChannelView,
  'channelId' | 'title' | 'logo' | 'categoryId' | 'groupKey'
>

/**
 * Registra view de canal pra rail "Canais recentes". Política (v2):
 *
 * - **Com `groupKey`**: chave da entrada é `scope::groupKey`. Múltiplas
 *   visualizações do mesmo grupo (mesmo canal em fontes diferentes)
 *   colapsam em 1 entrada, com `channelId` sempre apontando pra ÚLTIMA
 *   fonte usada (preserva contexto pro retomar).
 * - **Sem `groupKey`** (caller legado): chave é `scope::channelId`,
 *   comportamento antigo. Toda nova feature deve passar groupKey.
 */
/** Monta o record final de ChannelView (id/scope/schemaVersion/timestamp já
 *  resolvidos). O `count` é calculado pelo caller — difere entre o caminho IDB
 *  (herda de `existing` + v1 órfão) e o do fallback (herda do próprio fallback). */
function buildChannelViewRecord(
  view: ChannelViewInput,
  scope: string,
  id: string,
  count: number,
): ChannelView {
  const groupKey = view.groupKey
  return {
    id,
    providerScope: scope,
    channelId: view.channelId,
    title: view.title,
    logo: view.logo,
    categoryId: view.categoryId,
    ...(groupKey !== undefined ? { groupKey, schemaVersion: 2 } : { schemaVersion: 1 }),
    count,
    lastWatchedAt: Math.floor(Date.now() / 1000),
  }
}

export async function trackChannelView(view: ChannelViewInput): Promise<void> {
  const scope = currentScope()
  if (!scope) return
  const groupKey = view.groupKey
  const idKey = groupKey ?? view.channelId
  const id = `${scope}::${idKey}`
  try {
    const db = await getDb()
    const existing = await db.get('channelViews', id)

    // MAJOR #1 do review: limpar record v1 órfão da MESMA fonte quando
    // estamos escrevendo v2 (channelId vs groupKey IDs distintos). Sem isso:
    // - Usuário ativo pré-deploy tem v1 source-level (`scope::ch-123`)
    // - Pós-deploy assiste mesmo canal → write v2 (`scope::globo-sp`) cria record NOVO
    // - V1 fica órfão pra sempre, polui IDB, dedupeChannelViewsByGroup descarta
    //   silenciosamente mas `getRecentChannels(50)` puxa 50 records misturando
    //   órfãos → resultado visível pode ter < 50 entries.
    // Bônus: herda `count` do v1 pra não zerar histórico.
    let inheritedCount = 0
    if (groupKey !== undefined) {
      const legacyId = `${scope}::${view.channelId}`
      if (legacyId !== id) {
        const legacy = await db.get('channelViews', legacyId)
        if (legacy) {
          inheritedCount = legacy.count
          await db.delete('channelViews', legacyId).catch(() => {
            // delete-failed é fire-and-forget. Próxima passagem tenta de novo.
          })
        }
      }
    }

    await db.put(
      'channelViews',
      buildChannelViewRecord(view, scope, id, (existing?.count ?? 0) + inheritedCount + 1),
    )
    // IDB são → descarta cópia stale de fallback (device saiu da pressão de
    // quota, ou nunca esteve nela). Mantém o fallback enxuto — espelha `putPosition`.
    fallbackRemoveChannelView(id)
  } catch {
    // IDB rejeitou (quota estourada / "temporary storage" evictado pelo webOS — a
    // CAUSA do bug: os recentes NUNCA apareciam no device do dono, porque a
    // gravação falhava em silêncio). Espelha no localStorage (síncrono, quota
    // separada). NUNCA lança — o tracking é fire-and-forget e não pode quebrar o
    // player. O `count` herda do que já houver no fallback pra não zerar.
    const prev = fallbackReadChannelView(id)
    fallbackUpsertChannelView(buildChannelViewRecord(view, scope, id, (prev?.count ?? 0) + 1))
  }
}

/**
 * Lê canais recentes. Records v1 (sem groupKey) e v2 (com) coexistem sem
 * migração massiva — caller decide como tratar.
 *
 * Migração leve sugerida (não feita aqui pra manter store puro):
 * caller passa lista de `GroupedChannel` carregados do snapshot atual,
 * usa `dedupeChannelViewsByGroup(views, groups)` pra mapear records
 * v1 (legados) pro groupKey do snapshot, colapsando entradas duplicadas
 * que o user acumulou antes do schema v2.
 */
/** Mescla channelViews do IDB com o FALLBACK (localStorage) por id. Núcleo PURO
 *  (testável). Mesmo id nas duas fontes → vence o de `lastWatchedAt` MAIOR (mais
 *  recente); empate → mantém o IDB (canônico, evita flapping). Espelha
 *  `mergePositions`. */
export function mergeChannelViews(
  idb: ChannelView[],
  fallback: ChannelView[],
): ChannelView[] {
  const byId = new Map<string, ChannelView>()
  for (const c of idb) byId.set(c.id, c)
  for (const c of fallback) {
    const existing = byId.get(c.id)
    if (!existing) {
      byId.set(c.id, c)
      continue
    }
    byId.set(c.id, c.lastWatchedAt > existing.lastWatchedAt ? c : existing)
  }
  return [...byId.values()]
}

export async function getRecentChannels(limit = 10): Promise<ChannelView[]> {
  const scope = currentScope()
  if (!scope) return []
  // IDB é a fonte canônica. Tolerante a IDB quebrado (quota/eviction/piso Chr38):
  // se o read ESTOURAR, cai só no fallback — os recentes seguem aparecendo em vez
  // de sumirem em silêncio. Espelha `readMergedPositions`.
  let idb: ChannelView[] = []
  try {
    const db = await getDb()
    idb = await db.getAllFromIndex(
      'channelViews',
      'by-providerScope',
      IDBKeyRange.only(scope),
    )
  } catch {
    idb = []
  }
  return mergeChannelViews(idb, fallbackReadChannelViews(scope))
    .sort((a, b) => b.lastWatchedAt - a.lastWatchedAt)
    .slice(0, limit)
}

// ===== Manual sources (P1 #3 review auto-switch) =====
//
// Persiste a fonte ESCOLHIDA MANUALMENTE pelo user via SourceSwitcher.
// 1 registro por (provider, grupo) — chave estável é `groupKey` do
// GroupedChannel (não channelId source, que muda quando user escolhe
// outra fonte).
//
// Política B (decisão Fernando 2026-05-11): se a fonte manual ESGOTAR
// todas as tentativas numa sessão (todas as sources do grupo falharam
// começando pela manual), o registro é DELETADO + nova fonte que
// funcionou eventualmente vira default. Auto-switches durante a
// sessão NÃO gravam — só escolha consciente do user.

export async function getManualSource(groupKey: string): Promise<ManualSource | undefined> {
  const scope = currentScope()
  if (!scope) return undefined
  const db = await getDb()
  return db.get('manualSources', `${scope}::manual-source:${groupKey}`)
}

export async function saveManualSource(groupKey: string, channelId: string): Promise<void> {
  const scope = currentScope()
  if (!scope) return
  const db = await getDb()
  await db.put('manualSources', {
    id: `${scope}::manual-source:${groupKey}`,
    providerScope: scope,
    groupKey,
    channelId,
    chosenAt: Math.floor(Date.now() / 1000),
  })
}

export async function clearManualSource(groupKey: string): Promise<void> {
  const scope = currentScope()
  if (!scope) return
  const db = await getDb()
  await db.delete('manualSources', `${scope}::manual-source:${groupKey}`)
}

/** Limpa TUDO — só pra factory reset / debug. NÃO chamar em troca de provider. */
export async function clearLocalDb(): Promise<void> {
  fallbackClearAll()
  fallbackClearChannelViewsAll()
  const db = await getDb()
  await db.clear('positions')
  // Favoritos têm overlay com LÁPIDE: a ordem IDB → overlay mora no helper.
  await clearFavoritesStore({ kind: 'all' })
  await db.clear('channelViews')
  await db.clear('profiles')
  await db.clear('manualSources')
}

/**
 * Apaga apenas canais recentes (channelViews) do provider atual.
 * Preserva positions (Continue Assistindo de VOD/séries), favoritos, perfis.
 * Botão granular em Settings → Meus dados → Histórico — Canais.
 */
export async function clearChannelRecents(): Promise<void> {
  const scope = currentScope()
  const db = await getDb()
  if (!scope) {
    fallbackClearChannelViewsAll()
    await db.clear('channelViews')
    return
  }
  fallbackClearChannelViewsScope(scope)
  const keys = await db.getAllKeysFromIndex(
    'channelViews',
    'by-providerScope',
    IDBKeyRange.only(scope),
  )
  const tx = db.transaction('channelViews', 'readwrite')
  await Promise.all(keys.map((k) => tx.store.delete(k)))
  await tx.done
}

/**
 * Apaga histórico de filmes e séries (positions) do provider atual —
 * cobre tanto "em andamento" (finished=false) quanto "já assistidos"
 * (finished=true). Preserva channelViews, favoritos, perfis.
 * Botão granular em Settings → Meus dados → Histórico — Filmes e séries.
 */
export async function clearVodSeriesHistory(): Promise<void> {
  const scope = currentScope()
  const db = await getDb()
  if (!scope) {
    fallbackClearAll()
    await db.clear('positions')
    return
  }
  fallbackClearScope(scope)
  const keys = await db.getAllKeysFromIndex(
    'positions',
    'by-providerScope',
    IDBKeyRange.only(scope),
  )
  const tx = db.transaction('positions', 'readwrite')
  await Promise.all(keys.map((k) => tx.store.delete(k)))
  await tx.done
}

/**
 * Apaga histórico de visualização do provider atual: positions (Continue
 * Assistindo) + channelViews (Canais recentes). Preserva favoritos e perfis.
 * Botão dedicado em Settings → Meus dados.
 */
export async function clearWatchHistory(): Promise<void> {
  const scope = currentScope()
  const db = await getDb()
  if (!scope) {
    // Sem provider ativo: limpa global (improvável em uso normal)
    fallbackClearAll()
    fallbackClearChannelViewsAll()
    await db.clear('positions')
    await db.clear('channelViews')
    return
  }
  fallbackClearScope(scope)
  fallbackClearChannelViewsScope(scope)
  for (const store of ['positions', 'channelViews'] as const) {
    const keys = await db.getAllKeysFromIndex(
      store,
      'by-providerScope',
      IDBKeyRange.only(scope),
    )
    const tx = db.transaction(store, 'readwrite')
    await Promise.all(keys.map((k) => tx.store.delete(k)))
    await tx.done
  }
}

/**
 * Quais favoritos uma limpeza em massa cobre. UMA descrição gera as DUAS
 * pontas (o range do índice no IDB e o predicado do overlay), pra elas não
 * poderem discordar: um corte que apaga X do disco e esquece X no overlay (ou o
 * contrário) é o próprio defeito que o helper existe pra impedir.
 */
type FavoritesClearTarget =
  | { kind: 'scope'; scope: string }
  | { kind: 'provider'; providerPart: string }
  | { kind: 'all' }

function favoritesClearRange(target: FavoritesClearTarget): IDBKeyRange | null {
  if (target.kind === 'scope') return IDBKeyRange.only(target.scope)
  if (target.kind === 'provider') {
    const { lower, upper } = providerScopeBounds(target.providerPart)
    return IDBKeyRange.bound(lower, upper)
  }
  return null
}

function favoritesClearMatch(target: FavoritesClearTarget): (providerScope: string) => boolean {
  if (target.kind === 'scope') return (s) => s === target.scope
  if (target.kind === 'provider') {
    const { lower } = providerScopeBounds(target.providerPart)
    return (s) => s.startsWith(lower)
  }
  return () => true
}

/**
 * Apaga favoritos do IDB e fecha o overlay NA ORDEM QUE NÃO RESSUSCITA. É o
 * ÚNICO caminho de limpeza em massa da store `favorites` (há gate:
 * local-db.favorites-clear.gate.test.ts).
 *
 * ── O defeito que isto fecha ──
 * `clearAllFavorites` limpava o overlay, lápides incluídas, ANTES do IDB. Com a
 * cota em zero o delete aborta no commit, os registros ficam no disco, e as
 * lápides que escondiam os já desfavoritados tinham acabado de sair: depois de
 * relançar, "limpar favoritos" DEVOLVIA o item que o usuário tinha tirado. E
 * em silêncio (a tela de Configurações só dá `console.warn`).
 *
 * ── A ordem ──
 *  1. lê do índice o que a limpeza cobre (leitura funciona com a cota em zero);
 *  2. apaga numa transação e espera o COMMIT (`tx.done`, no mesmo `Promise.all`
 *     dos deletes: um request que falha primeiro não deixa o `done` rejeitar
 *     sem dono, o que viraria unhandled rejection);
 *  3a. aceitou → só AGORA o overlay esvazia;
 *  3b. recusou → o que a limpeza cobre vira LÁPIDE no overlay, inclusive cada
 *      registro lido no passo 1.
 *
 * @throws o erro do IDB quando a limpeza NÃO ficou garantida: as lápides não
 *   gravaram (localStorage também recusou), ou nem a leitura do passo 1 foi
 *   possível (aí não há como saber o que está no disco pra esconder; o que o
 *   overlay conhecia já virou lápide mesmo assim).
 */
async function clearFavoritesStore(target: FavoritesClearTarget): Promise<void> {
  const matches = favoritesClearMatch(target)
  let onDisk: Favorite[] = []
  let diskRead = false
  try {
    const db = await getDb()
    const range = favoritesClearRange(target)
    onDisk = range
      ? await db.getAllFromIndex('favorites', 'by-providerScope', range)
      : await db.getAll('favorites')
    diskRead = true
    const tx = db.transaction('favorites', 'readwrite')
    await Promise.all([...onDisk.map((f) => tx.store.delete(f.id)), tx.done])
  } catch (error: unknown) {
    const tombstoned = fallbackTombstoneFavoritesWhere(matches, onDisk)
    if (!tombstoned || !diskRead) throw error
    return
  }
  if (!fallbackClearFavoritesWhere(matches)) {
    // O disco já saiu; o que sobrou no overlay são adds que nunca chegaram lá
    // e continuariam aparecendo. Não dá pra dizer "limpo".
    throw new Error('favorites-fallback: o overlay não gravou a limpeza')
  }
}

/**
 * Apaga todos os favoritos do provider atual. Preserva positions/perfis.
 * Botão dedicado em Settings → Meus dados.
 *
 * Resolve se a limpeza ficou garantida em alguma fonte (IDB, ou lápides no
 * overlay quando o IDB recusa); rejeita quando nenhuma das duas. Ver
 * `clearFavoritesStore`.
 */
export async function clearAllFavorites(): Promise<void> {
  const scope = currentScope()
  await clearFavoritesStore(scope ? { kind: 'scope', scope } : { kind: 'all' })
}

/**
 * Apaga só dados do provider especificado (favoritos, positions, channelViews).
 * Profiles ficam intactos — usuário pode voltar pro provider e ainda ter perfis.
 * Usado em casos extremos (limpar dados antes de re-importar, etc).
 */
export async function clearLocalDbForProvider(providerScope: string): Promise<void> {
  fallbackClearScope(providerScope)
  fallbackClearChannelViewsScope(providerScope)
  // Favoritos fora do laço: a ordem IDB → overlay (lápides) mora no helper.
  await clearFavoritesStore({ kind: 'scope', scope: providerScope })
  const db = await getDb()
  for (const store of [
    'positions',
    'channelViews',
    'manualSources',
  ] as const) {
    const keys = await db.getAllKeysFromIndex(
      store,
      'by-providerScope',
      IDBKeyRange.only(providerScope),
    )
    const tx = db.transaction(store, 'readwrite')
    await Promise.all(keys.map((k) => tx.store.delete(k)))
    await tx.done
  }
}

/**
 * Limites (lower/upper) pra varrer o índice `by-providerScope` por PREFIXO de
 * provider — ou seja, TODOS os perfis daquele provider de uma vez.
 *
 * @param providerPart `p:<code>` ou `d:<listName>`.
 * @returns range `[lower, upper]` pra montar um `IDBKeyRange.bound()`.
 */
export function providerScopeBounds(providerPart: string): {
  lower: string
  upper: string
} {
  // O índice `by-providerScope` guarda o scope COMPLETO (`p:CODE::pf:ID`).
  // Filtrar por prefixo `p:CODE::` pega TODOS os perfis daquele provider
  // (inclusive o `pf:_main` de quem nunca escolheu perfil) e NENHUM de outro
  // provider. `￿` (U+FFFF) fecha o range: é o último code unit, então nenhuma
  // string com esse prefixo fica de fora na ordenação do IDB.
  //
  // Um `IDBKeyRange.only(scope)` aqui seria o bug clássico: limparia só o
  // perfil ATIVO e deixaria o histórico dos outros perfis no aparelho.
  return { lower: `${providerPart}::`, upper: `${providerPart}::￿` }
}

/**
 * Apaga TODO o dado PESSOAL do provider no aparelho: histórico (positions +
 * seriesProgress), favoritos, canais recentes, fonte manual escolhida e os
 * perfis da conta.
 *
 * **NÃO CHAMADA HOJE — E ISSO É INTENCIONAL. NÃO REMOVA COMO CÓDIGO MORTO.**
 * "Sair da conta" e "apagar os dados" são coisas diferentes (decisão do dono do
 * produto, 2026-07): o sign-out encerra a SESSÃO e o dado pessoal continua
 * salvo — por isso `sign-out.ts` cita esta função pra explicar por que NÃO a
 * chama, e há teste (`gate-escape.test.ts`) que REPROVA se alguém a plugar lá.
 * Ela é a base da ação EXPLÍCITA e futura de "apagar os dados deste aparelho"
 * (o "Limpar ..." do Settings hoje é granular; esta dá o corte por provider
 * inteiro, cobrindo todos os perfis de uma vez).
 *
 * **Deleta SÓ LOCAL, nunca no servidor**: usa ops cruas do IDB de propósito.
 * `deleteProfile()` enfileira um delete no outbox do sync — isso apagaria os
 * perfis do usuário NA NUVEM (em todos os devices dele). Aqui a conta é
 * preservada; some só a cópia deste aparelho.
 *
 * **NÃO toca no cache de catálogo** (snapshot / vod-info / series-info): é dado
 * do PROVEDOR, não da pessoa, e é caro de refazer numa TV.
 *
 * @param providerPart `p:<code>` ou `d:<listName>` — limpa todos os perfis
 *   desse provider (o índice guarda o scope COMPLETO `p:X::pf:Y`, então o
 *   filtro é por PREFIXO, via `providerScopeBounds()`; um `only()` pegaria só o
 *   perfil ativo e deixaria o dado dos outros perfis pra trás).
 * @param account `${providerCode}:${username}` da conta, ou null quando não há
 *   sessão Xtream (nada de perfil pra limpar).
 */
export async function clearPersonalDataForProvider(
  providerPart: string,
  account: string | null,
): Promise<void> {
  fallbackClearProviderPrefix(providerPart)
  fallbackClearChannelViewsProviderPrefix(providerPart)
  // Favoritos fora do laço: a ordem IDB → overlay (lápides) mora no helper, e
  // o corte por prefixo sai da MESMA `providerScopeBounds` usada abaixo.
  await clearFavoritesStore({ kind: 'provider', providerPart })
  const db = await getDb()

  const { lower, upper } = providerScopeBounds(providerPart)
  const range = IDBKeyRange.bound(lower, upper)

  for (const store of [
    'positions',
    'channelViews',
    'seriesProgress',
    'manualSources',
  ] as const) {
    const keys = await db.getAllKeysFromIndex(store, 'by-providerScope', range)
    const tx = db.transaction(store, 'readwrite')
    await Promise.all(keys.map((k) => tx.store.delete(k)))
    await tx.done
  }

  // Perfis (nome, avatar, PIN) são dado pessoal. Delete CRU: nada de outbox.
  if (account) {
    const keys = await db.getAllKeysFromIndex('profiles', 'by-account', account)
    const tx = db.transaction('profiles', 'readwrite')
    await Promise.all(keys.map((k) => tx.store.delete(k)))
    await tx.done
  }
}

// ===== Profiles =====

export const MAX_PROFILES = 4

export async function getProfilesForAccount(account: string): Promise<Profile[]> {
  const db = await getDb()
  const all = await db.getAllFromIndex('profiles', 'by-account', account)
  return all.sort((a, b) => a.createdAt - b.createdAt)
}

/** Busca um perfil pelo id. Usado pelo sync (provisionar perfil remoto). */
export async function getProfileById(profileId: string): Promise<Profile | undefined> {
  const db = await getDb()
  return db.get('profiles', profileId)
}

/** Extrai o providerCode do `account` (`${providerCode}:${username}`). null pra
 *  contas que não são de provedor com código (sync de perfil só vale pra essas). */
function providerCodeFromAccount(account: string): string | null {
  const code = account.split(':')[0]
  if (!code || code === 'direct') return null
  return code
}

export async function createProfile(
  account: string,
  data: { name: string; avatar: string; kind: 'adult' | 'kids' },
): Promise<Profile> {
  const capture = captureWrite()
  const db = await getDb()
  const existing = await db.getAllFromIndex('profiles', 'by-account', account)
  if (existing.length >= MAX_PROFILES) {
    throw new Error('max_profiles')
  }
  const profile: Profile = {
    // crypto.randomUUID direto quebra no WebView Chr38/53 (webOS 2017/2019);
    // generateUuidV4 tem feature-detect + fallback Math.random.
    id: generateUuidV4(),
    account,
    name: data.name.trim().slice(0, 30),
    avatar: data.avatar,
    kind: data.kind,
    createdAt: Math.floor(Date.now() / 1000),
  }
  await db.put('profiles', profile)
  // Sync: PROVISIONA o perfil remoto (swipe_profiles) — sem isso o push de itens
  // desse perfil tomaria 403 (profile_not_owned). Best-effort.
  void enqueueProfileUpsert({
    id: profile.id,
    name: profile.name,
    avatar: profile.avatar,
    kind: profile.kind,
    providerCode: providerCodeFromAccount(account),
    updatedAtSec: profile.createdAt,
    capture,
  })
  return profile
}

/**
 * Apaga os dados sincronizáveis de UM perfil (positions/favorites/seriesProgress
 * scopados por `p:CODE::pf:ID`). Evita itens órfãos no IDB quando o perfil é
 * excluído (importante em vlow Tizen com eviction LRU). channelViews/manualSources
 * NÃO entram (telemetria/escolha de fonte, não são "dados do perfil" sincronizados).
 *
 * Os favoritos vão pelo helper, que leva o OVERLAY junto (antes só o IDB saía,
 * e o overlay do perfil excluído ficava no localStorage, disputando o teto com
 * os perfis vivos). Mesmo helper, e não um "limpa o overlay antes" próprio: um
 * perfil excluído não é lido de novo, mas a correção desta função não deve
 * depender de o chamador ter apagado o perfil ANTES dos dados.
 */
async function clearProfileScopedData(providerCode: string, profileId: string): Promise<void> {
  const scope = `p:${providerCode}::pf:${profileId}`
  fallbackClearScope(scope)
  await clearFavoritesStore({ kind: 'scope', scope })
  const db = await getDb()
  for (const store of ['positions', 'seriesProgress'] as const) {
    const keys = await db.getAllKeysFromIndex(store, 'by-providerScope', IDBKeyRange.only(scope))
    const tx = db.transaction(store, 'readwrite')
    await Promise.all(keys.map((k) => tx.store.delete(k)))
    await tx.done
  }
}

export async function deleteProfile(profileId: string): Promise<void> {
  const capture = captureWrite()
  const db = await getDb()
  const profile = await db.get('profiles', profileId)
  await db.delete('profiles', profileId)
  // Limpa os itens do perfil (evita órfãos). Só dá pra compor o scope se a conta
  // é de provedor com código (não lista direta).
  if (profile) {
    const code = providerCodeFromAccount(profile.account)
    if (code) await clearProfileScopedData(code, profileId)
  }
  void enqueueProfileDelete(profileId, undefined, capture)
}

export async function updateProfile(
  profileId: string,
  patch: Partial<Pick<Profile, 'name' | 'avatar' | 'kind'>>,
): Promise<void> {
  const capture = captureWrite()
  const db = await getDb()
  const p = await db.get('profiles', profileId)
  if (!p) return
  const updated = { ...p, ...patch }
  await db.put('profiles', updated)
  void enqueueProfileUpsert({
    id: updated.id,
    name: updated.name,
    avatar: updated.avatar,
    kind: updated.kind,
    providerCode: providerCodeFromAccount(updated.account),
    // updatedAt = agora (edição), pra ganhar do estado remoto via LWW.
    capture,
  })
}

/**
 * Atualiza só o `hasPin` de um perfil no local-db (reflexo OTIMISTA após
 * definir/remover PIN pelos endpoints dedicados). NÃO re-enfileira no sync: o
 * servidor já é a fonte da verdade do PIN (o endpoint dedicado gravou o hash);
 * o próximo pull confirma. db.put direto, sem tocar nos outros campos.
 */
export async function setProfileHasPin(profileId: string, hasPin: boolean): Promise<void> {
  const db = await getDb()
  const p = await db.get('profiles', profileId)
  if (!p) return
  await db.put('profiles', { ...p, hasPin })
}

// ===== Apply de itens REMOTOS (puller do sync — S4) =====
//
// Estas funções aplicam dados vindos do servidor no local-db SEM re-enfileirar no
// outbox (db.put/delete diretos — senão pull→apply→enqueue→push viraria loop).
// Merge por LWW de CONTEÚDO: o timestamp do próprio record (lastWatchedAt/addedAt)
// decide quem vence — semanticamente correto ("o mais recente que assisti/favoritei
// vence"). Tombstone remoto (deleted) remove o local se for mais novo.

/** Timestamp de conteúdo de um record (proxy do "quando" pra LWW). */
function recordTimestamp(kind: SyncKind, payload: unknown): number {
  const p = payload as Record<string, unknown> | null
  if (!p) return 0
  if (kind === 'favorite') return typeof p['addedAt'] === 'number' ? p['addedAt'] : 0
  // position / series_progress / watched
  return typeof p['lastWatchedAt'] === 'number' ? p['lastWatchedAt'] : 0
}

/** Store do IDB pra cada kind de sync. */
function storeForKind(kind: SyncKind): 'positions' | 'favorites' | 'seriesProgress' | null {
  if (kind === 'position' || kind === 'watched') return 'positions'
  if (kind === 'favorite') return 'favorites'
  if (kind === 'series_progress') return 'seriesProgress'
  return null
}

/**
 * Aplica UM item remoto no local-db (merge LWW). `localId` é a key local já
 * recomposta pelo puller (composeLocalId com o perfil/provider deste device).
 * `scope` é o providerScope local correspondente. Retorna 'applied' | 'skipped'.
 *
 * - deleted=true → remove o local SEM comparar data. O tombstone chega sem payload
 *   (`recordTimestamp` = 0) e esta função não recebe o `updatedAt` do servidor, então
 *   não há com o que comparar: um item local mais novo AINDA NÃO ENVIADO é apagado
 *   aqui (volta quando o outbox deste device o empurrar e o pull o trouxer de novo —
 *   o LWW do servidor, que compara `updated_at`, é quem decide). Corrigido em
 *   27/09/2026: o comentário anterior dizia "remove SE o remoto for mais novo", uma
 *   defesa que o código nunca teve. Por isso o tombstone da migração de carimbos leva
 *   o relógio do carimbo, não o de agora (ver autoadvance-stamp-migration.ts).
 * - upsert → grava SE o remoto for mais novo que o local (ou não houver local).
 * O record gravado tem `id`/`providerScope` REESCRITOS pros valores locais (o
 * payload remoto traz os do device de origem — ignoramos e re-scopamos).
 */
export async function applyRemoteSyncItem(args: {
  localId: string
  scope: string
  kind: SyncKind
  payload: unknown
  deleted: boolean
}): Promise<'applied' | 'skipped'> {
  const store = storeForKind(args.kind)
  if (!store) return 'skipped'
  const db = await getDb()

  if (args.deleted) {
    const existing = await db.get(store, args.localId)
    if (!existing) return 'skipped' // já não existe
    await db.delete(store, args.localId)
    return 'applied'
  }

  if (!args.payload || typeof args.payload !== 'object') return 'skipped'
  const remoteTs = recordTimestamp(args.kind, args.payload)
  const existing = await db.get(store, args.localId)
  if (existing) {
    const localTs = recordTimestamp(args.kind, existing)
    // LWW de conteúdo: só sobrescreve se o remoto for ESTRITAMENTE mais novo.
    // Empate → mantém o local (evita flapping entre devices com mesmo timestamp).
    if (remoteTs <= localTs) return 'skipped'
  }
  // Posição remota sem duração finita: a MESMA regra do `savePosition` pro PROGRESSO.
  // O JSON não carrega `Infinity` (vira `null`), então a posição que um app antigo de
  // outro aparelho gravou com duração `Infinity` chega com `durationSec: null` e
  // deixaria o item "em andamento" pra sempre aqui também: essa não entra. Mas o ✓
  // entra: `finished: true` sem duração (app antigo em stream sem ENDLIST, ou
  // reassistido num stream sem duração) aplica, com a duração normalizada no formato
  // do "Já vi" (1/1). Pular o ✓ o perdia de vez: o cursor do pull passa por ele.
  let normalized: { positionSec: number; durationSec: number } | null = null
  if (store === 'positions') {
    const p = args.payload as { positionSec?: unknown; durationSec?: unknown; finished?: unknown }
    if (!isPersistableProgress(Number(p.positionSec), Number(p.durationSec))) {
      if (p.finished !== true) return 'skipped'
      normalized = { positionSec: 1, durationSec: 1 }
    }
  }
  // Reescreve id/providerScope pros valores LOCAIS (o payload traz os do device
  // de origem). O resto do conteúdo (positionSec, finished, title, poster...) vem
  // do remoto.
  const record = {
    ...(args.payload as object),
    ...(normalized ?? {}),
    id: args.localId,
    providerScope: args.scope,
  }
  // INVARIANTE de `finished` (positions): igual ao savePosition, `finished` é
  // monotônico — progresso parcial NUNCA desmarca um "assistido". Se o local já
  // estava finished, preserva mesmo que o remoto (mais novo) seja parcial. Sem
  // isso, "assistir de novo" em outro device tiraria o ✓ aqui.
  if ((store === 'positions') && (existing as PlaybackPosition | undefined)?.finished === true) {
    ;(record as PlaybackPosition).finished = true
  }
  // INVARIANTE análoga em series_progress: `allEpisodesWatched` também é
  // monotônico (reassistir o último ep em outro device não deve "desmarcar" a
  // série inteira aqui). Auto-corrigível via recompute, mas preservamos pela raiz.
  if (
    store === 'seriesProgress' &&
    (existing as SeriesProgress | undefined)?.allEpisodesWatched === true
  ) {
    ;(record as SeriesProgress).allEpisodesWatched = true
  }
  await db.put(store, record as never)
  return 'applied'
}

/**
 * Adota um perfil REMOTO no local-db (reconciliação de uuid entre devices). O
 * puller chama isso ao baixar /profiles: garante que o perfil exista localmente
 * com o MESMO uuid do servidor — sem isso os itens de sync (chaveados por
 * profileId) não casariam entre devices. Tombstone remoto remove o local.
 * Não re-enfileira (db direto). LWW por `createdAt` não se aplica a perfis
 * (campos mutáveis name/avatar/kind) — usa o estado remoto como verdade do merge.
 * Retorna true se REALMENTE mudou algo (criou/editou/excluiu) — o puller usa isso
 * pra só disparar o refresh da UI quando há mudança real (evita refetch inútil).
 */
export async function applyRemoteProfile(args: {
  id: string
  account: string
  name: string
  avatar: string
  kind: 'adult' | 'kids'
  hasPin: boolean
  createdAt: number
  deleted: boolean
}): Promise<boolean> {
  const db = await getDb()
  if (args.deleted) {
    const existing = await db.get('profiles', args.id)
    if (!existing) return false // já não existia → nada mudou
    await db.delete('profiles', args.id)
    // Limpa os itens órfãos do perfil excluído (mesmo que o deleteProfile local).
    const code = providerCodeFromAccount(args.account)
    if (code) await clearProfileScopedData(code, args.id)
    return true
  }
  const existing = await db.get('profiles', args.id)
  const record: Profile = {
    id: args.id,
    account: args.account,
    name: args.name,
    avatar: args.avatar,
    kind: args.kind,
    // Server é a fonte da verdade do PIN — sempre reflete o estado remoto.
    hasPin: args.hasPin,
    createdAt: existing?.createdAt ?? args.createdAt,
  }
  // Mudou de fato? (perfil novo, ou algum campo mutável diferente — inclui hasPin
  // pra a tela de perfis refletir PIN definido/removido em outro device).
  const changed =
    !existing ||
    existing.name !== record.name ||
    existing.avatar !== record.avatar ||
    existing.kind !== record.kind ||
    (existing.hasPin ?? false) !== record.hasPin
  await db.put('profiles', record)
  return changed
}

/**
 * Nome do banco deste módulo, pra quem precisa apagá-lo por NOME sem abri-lo:
 * a nova tentativa do "Limpar dados" no boot seguinte (hard-reset-pendente.ts),
 * quando o `hardResetLocalDb` não conseguiu. Exportado daqui, e não repetido
 * como literal lá, pra existir numa fonte só.
 */
export const LOCAL_DB_NAME = DB_NAME
